import {
  Container,
  Link,
  List,
  ListItem,
  Stack,
  Typography,
} from "@mui/material";
import type { FC, ReactNode } from "react";

export const slugify = (text: string): string =>
  text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const listSx = { listStyleType: "disc", pl: 4 } as const;
const listItemSx = { display: "list-item" } as const;
const headingScrollSx = { scrollMarginTop: 24 } as const;

type StaticPageLayoutProps = {
  children: ReactNode;
};

export const StaticPageLayout: FC<StaticPageLayoutProps> = ({ children }) => (
  <Container maxWidth="md">
    <Stack
      component="article"
      sx={{
        gap: 3,
        my: 5
      }}>
      {children}
    </Stack>
  </Container>
);

type StaticPageTitleProps = {
  children: ReactNode;
};

export const StaticPageTitle: FC<StaticPageTitleProps> = ({ children }) => (
  <Typography
    component="h1"
    variant="h4"
    sx={{
      textAlign: "center",
      fontWeight: 700
    }}>
    {children}
  </Typography>
);

type StaticPageListProps = {
  items: ReactNode[];
};

export const StaticPageList: FC<StaticPageListProps> = ({ items }) => (
  <List component="ul" sx={listSx}>
    {items.map((item, index) => (
      <ListItem
        key={index}
        component="li"
        disablePadding
        sx={listItemSx}
      >
        <Typography variant="caption">{item}</Typography>
      </ListItem>
    ))}
  </List>
);

type StaticPageParagraphProps = {
  children: ReactNode;
};

export const StaticPageParagraph: FC<StaticPageParagraphProps> = ({
  children,
}) => <Typography variant="caption">{children}</Typography>;

export type StaticPageH2Tone = "large" | "default" | "emphasized";
export type StaticPageH3Tone = "default" | "subtle";

type StaticPageHeadingProps = {
  title: string;
  level: 2 | 3;
  tone?: StaticPageH2Tone | StaticPageH3Tone;
};

export const StaticPageHeading: FC<StaticPageHeadingProps> = ({
  title,
  level,
  tone,
}) => {
  const id = slugify(title);

  if (level === 2) {
    const h2Tone = (tone as StaticPageH2Tone | undefined) ?? "default";

    if (h2Tone === "large") {
      return (
        <Typography
          id={id}
          component="h2"
          variant="h6"
          sx={[{
            fontWeight: 700
          }, ...(Array.isArray(headingScrollSx) ? headingScrollSx : [headingScrollSx])]}>
          {title}
        </Typography>
      );
    }

    return (
      <Typography
        id={id}
        component="h2"
        variant="caption"
        sx={[{
          fontWeight: h2Tone === "emphasized" ? 800 : 700
        }, ...(Array.isArray(headingScrollSx) ? headingScrollSx : [headingScrollSx])]}>
        {title}
      </Typography>
    );
  }

  const h3Tone = (tone as StaticPageH3Tone | undefined) ?? "default";

  return (
    <Typography
      id={id}
      component="h3"
      variant="caption"
      sx={[{
        fontWeight: h3Tone === "subtle" ? 700 : 800
      }, ...(Array.isArray(headingScrollSx) ? headingScrollSx : [headingScrollSx])]}>
      {title}
    </Typography>
  );
};

type StaticPageSubheadingProps = {
  title: string;
};

export const StaticPageSubheading: FC<StaticPageSubheadingProps> = ({
  title,
}) => (
  <Typography variant="caption" sx={{
    fontWeight: 700
  }}>
    {title}
  </Typography>
);

type StaticPageSectionProps = {
  id: string;
  children: ReactNode;
  gap?: number;
};

export const StaticPageSection: FC<StaticPageSectionProps> = ({
  id,
  children,
  gap = 1,
}) => (
  <Stack component="section" aria-labelledby={id} sx={{
    gap: gap
  }}>
    {children}
  </Stack>
);

type StaticPageNavProps = {
  children: ReactNode;
  label?: string;
  gap?: number;
};

export const StaticPageNav: FC<StaticPageNavProps> = ({
  children,
  label = "Table of contents",
  gap = 1,
}) => (
  <Stack component="nav" aria-label={label} sx={{
    gap: gap
  }}>
    {children}
  </Stack>
);

type StaticPageTocLinkProps = {
  href: string;
  children: ReactNode;
};

export const StaticPageTocLink: FC<StaticPageTocLinkProps> = ({
  href,
  children,
}) => (
  <Link href={href} variant="caption" color="info" underline="hover">
    {children}
  </Link>
);
